import { Trans, useLingui } from "@lingui/react/macro";
import type { LinkFavicons } from "@rakazo/chat-ui/web";
import { LinkFaviconsContext, RemoteImagesContext } from "@rakazo/chat-ui/web";
import { isMcpOAuthAppSession, LOCAL_SETTINGS_PAGE } from "@rakazo/contracts";
import { analyticsScreen } from "@rakazo/core";
import { Button, Skeleton } from "@rakazo/ui-web";
import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { LoadingState } from "./components/ai/primitives";
import { ContentLoading } from "./components/ContentLoading";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PwaInstall } from "./components/PwaInstall";
import { SubscriptionGate } from "./components/SubscriptionGate";
import { WebPushNotifications } from "./components/WebPushNotifications";
import { authClient } from "./lib/auth";
import { getTicketBoardEnabled, subscribeTicketBoardEnabled } from "./lib/bootstrap";
import { desktopBridge } from "./lib/desktop";
import { identifyMonitoringUser } from "./lib/monitoring";
import { markAfterPaint, markOnce } from "./lib/performance";
import { lazyComponent } from "./lib/preload-recovery";
import { getRemoteImagesEnabled, subscribeRemoteImages } from "./lib/remote-images-preference";
import { isRoomPrivacyPath, protectRoomReferrer } from "./lib/room-privacy";
import { rpc } from "./lib/rpc";
import {
  holdAnonymousGate,
  holdUnreachableGate,
  sessionGate,
  sessionRetryDelayMs,
  showSessionUnavailable,
} from "./lib/session-gate";
import { safeSignInDestination, spaceTransferSignInPath } from "./lib/space-transfer-handoff";
import { completeSsoCallback, SSO_CALLBACK_PATH } from "./lib/sso-flow";
import { useVideoBytesCacheScope } from "./lib/use-artifact-video-poster";
import { IntegrationSetupPage } from "./pages/IntegrationSetup";
import { LocalSettingsPage } from "./pages/LocalSettings";
import { McpOAuthCallbackPage } from "./pages/McpOAuthCallback";
import { ShellPage } from "./pages/Shell";

const SharedRoomsRoutes = lazyComponent(() =>
  import("./pages/SharedRoomsPage").then((module) => module.SharedRoomsRoutes),
);

const AuthPage = lazyComponent(() => import("./pages/Auth").then((module) => module.AuthPage));
const PasswordResetPage = lazyComponent(() =>
  import("./pages/Auth").then((module) => module.PasswordResetPage),
);
const OnboardingPage = lazyComponent(() =>
  import("./pages/Onboarding").then((module) => module.OnboardingPage),
);
const WelcomePage = lazyComponent(() =>
  import("./pages/Welcome").then((module) => module.WelcomePage),
);
const ArtifactsPage = lazyComponent(() =>
  import("./pages/Artifacts").then((module) => module.ArtifactsPage),
);
const SchedulePage = lazyComponent(() =>
  import("./pages/Schedule").then((module) => module.SchedulePage),
);
const SitesPage = lazyComponent(() => import("./pages/Sites").then((module) => module.SitesPage));
const BoardPage = lazyComponent(() => import("./pages/Board").then((module) => module.BoardPage));
const InvitesPage = lazyComponent(() =>
  import("./pages/InvitesPage").then((module) => module.InvitesPage),
);

function SsoCallbackPage() {
  useEffect(() => {
    completeSsoCallback();
  }, []);
  return null;
}

// Link icons come from our API, which fetches and caches them; the browser never asks the site.
const linkFavicons: LinkFavicons = {
  load: (origin) => rpc.links.favicon({ origin }),
};

export function App() {
  const roomRoute = isRoomPrivacyPath(useLocation().pathname);
  useLayoutEffect(() => {
    if (roomRoute) protectRoomReferrer();
  }, [roomRoute]);
  const loadRemoteImages = useSyncExternalStore(
    subscribeRemoteImages,
    getRemoteImagesEnabled,
    () => false,
  );
  const location = useLocation();
  // The mobile app's auth browser is usually not signed in here, and the page only hands the
  // result back to the app, so this return needs no session.
  const mcpAppReturn =
    location.pathname === "/mcp/oauth/callback" &&
    isMcpOAuthAppSession(new URLSearchParams(location.search).get("state") ?? "");
  return (
    <RemoteImagesContext.Provider value={loadRemoteImages}>
      <LinkFaviconsContext.Provider value={linkFavicons}>
        <ErrorBoundary fallback={<AppFailed />}>
          {roomRoute ? (
            <Suspense fallback={<ContentLoading />}>
              <SharedRoomsRoutes />
            </Suspense>
          ) : window.location.pathname === SSO_CALLBACK_PATH ? (
            <SsoCallbackPage />
          ) : window.location.pathname === LOCAL_SETTINGS_PAGE ? (
            <LocalSettingsPage />
          ) : mcpAppReturn ? (
            <McpOAuthCallbackPage />
          ) : (
            <SessionApp />
          )}
        </ErrorBoundary>
        {roomRoute ? null : <PwaInstall />}
      </LinkFaviconsContext.Provider>
    </RemoteImagesContext.Provider>
  );
}

function SessionApp() {
  const [searchParams] = useSearchParams();
  const signInDestination = safeSignInDestination(searchParams.get("next"));
  const ticketBoardEnabled = useSyncExternalStore(
    subscribeTicketBoardEnabled,
    getTicketBoardEnabled,
    () => false,
  );
  const session = authClient.useSession();
  const location = useLocation();
  const pathname = location.pathname;
  const screen = analyticsScreen(pathname);
  const analyticsUserId = session.data?.user.id;
  useVideoBytesCacheScope(analyticsUserId);
  const lastScreen = useRef<string | undefined>(undefined);
  useEffect(() => {
    // A pending session is not a sign-out; startup already tagged the signed-in user.
    if (session.isPending) return;
    void identifyMonitoringUser(Boolean(analyticsUserId));
  }, [analyticsUserId, session.isPending]);
  useEffect(() => {
    if (!analyticsUserId) {
      lastScreen.current = undefined;
      return;
    }
    if (!screen) return;
    const key = `${analyticsUserId}:${screen}`;
    if (lastScreen.current === key) return;
    lastScreen.current = key;
    void Promise.resolve()
      .then(() => rpc.analytics.screen({ screen, surface: desktopBridge() ? "desktop" : "web" }))
      .catch(() => undefined);
  }, [analyticsUserId, screen]);
  const gate = sessionGate(session);
  const [holdingUnreachable, setHoldingUnreachable] = useState(false);
  const nextHolding = holdUnreachableGate(gate, holdingUnreachable);
  if (nextHolding !== holdingUnreachable) setHoldingUnreachable(nextHolding);
  const [holdingAnonymous, setHoldingAnonymous] = useState(false);
  const nextHoldingAnonymous = holdAnonymousGate(gate, holdingAnonymous);
  if (nextHoldingAnonymous !== holdingAnonymous) setHoldingAnonymous(nextHoldingAnonymous);

  useLayoutEffect(() => {
    if (session.isPending) return;
    markOnce("rk:renderer:session-committed");
    markAfterPaint("rk:renderer:session-painted");
  }, [session.isPending]);

  if (showSessionUnavailable(gate, nextHolding)) {
    return <SessionUnavailable refetch={session.refetch} />;
  }
  if (gate === "loading" && !nextHoldingAnonymous) {
    return window.location.pathname.startsWith("/app") ? (
      <ShellSkeleton />
    ) : (
      <div
        className="grid h-full place-items-center text-muted-foreground/80"
        data-rakazo-app-state="session-pending"
      >
        <ContentLoading />
      </div>
    );
  }

  const user = session.data?.user;
  return (
    <div className="h-full" data-rakazo-app-state="ready">
      <Suspense
        fallback={
          <div className="flex h-full bg-background">
            <ContentLoading />
          </div>
        }
      >
        <Routes>
          <Route path="/" element={user ? <Navigate to="/app" replace /> : <WelcomePage />} />
          <Route
            path="/sign-in"
            element={
              user ? <Navigate to={signInDestination} replace /> : <AuthPage key="in" mode="in" />
            }
          />
          <Route
            path="/sign-up"
            element={user ? <Navigate to="/onboarding" replace /> : <AuthPage key="up" mode="up" />}
          />
          <Route
            path="/forgot-password"
            element={
              user ? <Navigate to="/app" replace /> : <AuthPage key="forgot" mode="forgot" />
            }
          />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route
            path="/invites"
            element={user ? <InvitesPage /> : <Navigate to="/sign-in?next=/invites" replace />}
          />
          <Route
            path="/mcp/oauth/callback"
            element={user ? <McpOAuthCallbackPage /> : <Navigate to="/sign-in" replace />}
          />
          <Route
            path="/integrations/setup"
            element={
              user ? (
                <IntegrationSetupPage />
              ) : (
                <Navigate to="/sign-in?next=/integrations/setup" replace />
              )
            }
          />
          <Route
            element={
              user ? (
                <SubscriptionGate fallback={<GateFallback />}>
                  <Outlet />
                </SubscriptionGate>
              ) : (
                <Navigate to={spaceTransferSignInPath(pathname, location.search)} replace />
              )
            }
          >
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/app" element={<ShellPage />} />
            <Route path="/app/g/:groupId" element={<ShellPage />} />
            <Route path="/app/schedule" element={<SchedulePage />} />
            <Route path="/app/artifacts" element={<ArtifactsPage />} />
            <Route path="/app/sites" element={<SitesPage />} />
            <Route path="/app/artifacts/:artifactId" element={<ArtifactsPage />} />
            {ticketBoardEnabled ? <Route path="/app/board" element={<BoardPage />} /> : null}
            <Route path="/app/:botId" element={<ShellPage />} />
          </Route>
        </Routes>
      </Suspense>
      {user ? <WebPushNotifications /> : null}
    </div>
  );
}

/**
 * A session lookup that never reached the server is not a sign-out, so the app
 * waits and retries here instead of routing to sign-in and stranding a signed-in
 * user. Better Auth only polls once a session exists, so the retry lives here.
 */
function SessionUnavailable({ refetch }: { refetch: () => Promise<void> }) {
  const { t } = useLingui();
  const [attempt, setAttempt] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const retryImmediately = useRef(false);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    let cancelled = false;
    const delay = retryImmediately.current ? 0 : sessionRetryDelayMs(attempt);
    retryImmediately.current = false;
    const timer = setTimeout(() => {
      void refetchRef.current().finally(() => {
        if (!cancelled) setAttempt((value) => value + 1);
      });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [attempt, retryKey]);

  return (
    <div className="grid h-full place-items-center bg-background px-6 text-center">
      <div className="flex flex-col items-center">
        <LoadingState label={t`Reconnecting`} />
        <p className="mt-3 text-[13.5px] text-muted-foreground/80">
          <Trans>Can&apos;t reach the server.</Trans>
        </p>
        <div className="mt-4">
          <Button
            variant="secondary"
            className="rounded-full"
            onClick={() => {
              retryImmediately.current = true;
              setAttempt(0);
              setRetryKey((key) => key + 1);
            }}
          >
            <Trans>Retry now</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Last resort for a render failure no inner boundary caught: offer a reload instead of a blank page. */
function AppFailed() {
  return (
    <div
      className="grid h-full place-items-center bg-background px-6 text-center"
      data-rakazo-app-state="failed"
    >
      <div className="flex flex-col items-center">
        <p className="text-[13.5px] text-muted-foreground/80">
          <Trans>Something went wrong. Try again.</Trans>
        </p>
        <div className="mt-4">
          <Button
            variant="secondary"
            className="rounded-full"
            onClick={() => window.location.reload()}
          >
            <Trans>Refresh</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

function GateFallback() {
  return window.location.pathname.startsWith("/app") ? (
    <ShellSkeleton />
  ) : (
    <div className="flex h-full bg-background">
      <ContentLoading />
    </div>
  );
}

function ShellSkeleton() {
  return (
    <div
      className="flex h-full overflow-hidden bg-background"
      data-rakazo-app-state="session-pending"
    >
      <aside className="hidden w-[316px] shrink-0 border-e border-sidebar-border bg-sidebar px-3.5 pt-16 md:block">
        <Skeleton className="h-10 rounded-xl" />
        <div className="mt-5 space-y-2 px-1">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex items-center gap-3 rounded-xl px-2 py-2.5">
              <Skeleton className="size-9 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-2/5" />
                <Skeleton className="h-2.5 w-4/5" />
              </div>
            </div>
          ))}
        </div>
      </aside>
      <main className="flex flex-1 flex-col" aria-busy="true">
        <div className="h-[74px] border-b border-sidebar-border" />
        <ContentLoading conversation />
        <div className="mx-6 mb-6 h-[54px] rounded-full border border-border bg-background" />
      </main>
    </div>
  );
}
